import { FunctionComponent, KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ReferenceCustomer, ReferenceCustomersResponse } from "../../schema/icp";

import { distinctSeedKinds } from "./referenceCustomerKinds";

/** Mirrors MAX_REFERENCE_CUSTOMERS in the API — the seeds are capped on both ends. */
export const MAX_REFERENCE_CUSTOMERS = 5;

interface ReferenceCustomerSeedsProps {
	customers: ReferenceCustomer[];
	/** The named companies are not all the same kind of business (PREC-3896). */
	mixed?: boolean;
	/** Persists the edited list and returns it re-resolved, or null when the save failed. */
	onSave: (names: string[]) => Promise<ReferenceCustomersResponse | null>;
	/** The user is happy with the seeds — the chat script moves on. */
	onConfirm: () => void;
}

/**
 * The lookalike seeds, editable inside the chat (PREC-3861): the companies we
 * resolved as chips the user can drop, plus an input to add the ones we missed.
 *
 * They seed the lookalike search, so a mixed bag ("an investment agency AND a
 * software house") produces useless matches — hence the hint, and hence the
 * confirm step: the list recommendation is only fetched once the user is done.
 *
 * PREC-3896: asking for companies that resemble each other was not enough, so a
 * mixed list now says so, naming the kinds it saw. It is a warning and not a
 * block — the user may know better than the extraction did.
 */
export const ReferenceCustomerSeeds: FunctionComponent<ReferenceCustomerSeedsProps> = ({
	customers,
	mixed = false,
	onSave,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const ts = (key: string, options?: Record<string, unknown>) =>
		t(`onboarding.chat.seeds.${key}`, options);

	const [seeds, setSeeds] = useState<ReferenceCustomer[]>(customers);
	const [isMixed, setIsMixed] = useState(mixed);
	const [draft, setDraft] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [isConfirmed, setIsConfirmed] = useState(false);

	const persist = async (names: string[]) => {
		setIsSaving(true);
		const saved = await onSave(names);

		setIsSaving(false);

		if (saved) {
			setSeeds(saved.customers);
			// Recomputed on the save's response: removing the odd one out has to clear the warning,
			// or it reads as "nothing I do helps".
			setIsMixed(saved.mixed === true);
		}
	};

	const remove = (name: string) => {
		void persist(seeds.filter((seed) => seed.name !== name).map((seed) => seed.name));
	};

	const commitDraft = () => {
		const value = draft.trim();

		setDraft("");

		if (!value || seeds.some((seed) => seed.name.toLowerCase() === value.toLowerCase())) {
			return;
		}

		void persist([...seeds.map((seed) => seed.name), value]);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter" || event.key === ",") {
			event.preventDefault();
			commitDraft();
		}
	};

	const confirm = () => {
		setIsConfirmed(true);
		onConfirm();
	};

	const isFull = seeds.length >= MAX_REFERENCE_CUSTOMERS;

	const kinds = distinctSeedKinds(seeds);

	return (
		<div className="mt-2 flex flex-col gap-2">
			{isMixed && !isConfirmed && kinds.length > 1 && (
				<p
					className="rounded-lg border border-tag-warning-border bg-background-warning px-3 py-2 text-xs text-tag-warning-dark"
					data-testid="reference-customers-mixed"
				>
					{ts("mixedWarning", { kinds: kinds.join(", ") })}
				</p>
			)}
			<ul className="flex flex-wrap gap-1.5">
				{seeds.map((seed) => (
					<li
						key={seed.name}
						className="flex max-w-full items-center gap-1.5 rounded-md bg-white px-2 py-1 text-xs font-medium"
					>
						<span className="max-w-[240px] truncate">
							{seed.domain ? `${seed.name} (${seed.domain})` : seed.name}
						</span>
						{!isConfirmed && (
							<button
								aria-label={t("onboarding.chat.seeds.remove", { name: seed.name })}
								className="cursor-pointer text-text-meta hover:text-status-error"
								disabled={isSaving}
								type="button"
								onClick={() => remove(seed.name)}
							>
								×
							</button>
						)}
					</li>
				))}
			</ul>

			{!isConfirmed && (
				<>
					{!isFull && (
						<input
							className="input-styles h-8 w-full rounded-lg border bg-white px-2 text-xs"
							disabled={isSaving}
							placeholder={ts("addPlaceholder")}
							value={draft}
							onBlur={commitDraft}
							onChange={(event) => setDraft(event.target.value)}
							onKeyDown={handleKeyDown}
						/>
					)}
					<p className="text-xs italic text-text-meta">{ts("hint")}</p>
					<button
						className="cursor-pointer self-start rounded-full border border-brand-light px-3.5 py-1.5 text-xs font-semibold text-brand-light transition-colors duration-200 hover:bg-blue-action hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
						disabled={isSaving || seeds.length === 0}
						type="button"
						onClick={confirm}
					>
						{ts("confirm")}
					</button>
				</>
			)}
		</div>
	);
};
